import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true and disabled
    functional: true,
    analytics: true,
    advertising: false,
  });

  useEffect(() => {
    // Check if user has already set cookie preferences
    const consentGiven = localStorage.getItem('cookieConsentGiven');
    
    // If no consent has been given yet, show the banner after a short delay
    if (!consentGiven) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500); // Show after 1.5 seconds
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    setPreferences({
      essential: true,
      functional: true,
      analytics: true,
      advertising: true,
    });
    
    savePreferences({
      essential: true,
      functional: true,
      analytics: true,
      advertising: true,
    });
  };

  const handleRejectNonEssential = () => {
    setPreferences({
      essential: true,
      functional: false,
      analytics: false,
      advertising: false,
    });
    
    savePreferences({
      essential: true,
      functional: false,
      analytics: false,
      advertising: false,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const savePreferences = (prefs) => {
    // In a real implementation, this would set cookies based on preferences
    localStorage.setItem('cookieConsentGiven', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    setIsVisible(false);
  };

  const handleToggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const handlePreferenceChange = (category) => {
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-xs bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
      <div className="p-3">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center">
            <Cookie className="h-4 w-4 text-ta-green mr-1.5" />
            <h3 className="text-sm font-semibold text-gray-900">Cookie Settings</h3>
          </div>
          <button 
            onClick={() => setIsVisible(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        
        <div className="text-xs text-gray-600 mb-3">
          <p>
            We use cookies to enhance your experience. By continuing, you agree to our use of cookies.
          </p>
        </div>
        
        <div className="mb-3">
          <button
            onClick={handleToggleDetails}
            className="flex items-center text-xs text-ta-green hover:underline"
          >
            {showDetails ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                Hide Details
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                Customize
              </>
            )}
          </button>
        </div>
        
        {showDetails && (
          <div className="mb-3 space-y-2 border-t border-b border-gray-200 py-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-900">Essential</p>
                <p className="text-[10px] text-gray-500">Required for functionality</p>
              </div>
              <div className="relative inline-block w-8 mr-1 align-middle">
                <input 
                  type="checkbox" 
                  checked={preferences.essential} 
                  disabled
                  className="opacity-0 w-0 h-0 absolute"
                  id="toggle-essential"
                />
                <label 
                  htmlFor="toggle-essential" 
                  className="block overflow-hidden h-5 rounded-full bg-ta-green cursor-not-allowed"
                >
                  <span className="block h-5 w-5 rounded-full bg-white transform translate-x-3 transition-transform"></span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-900">Functional</p>
                <p className="text-[10px] text-gray-500">For enhanced features</p>
              </div>
              <div className="relative inline-block w-8 mr-1 align-middle">
                <input 
                  type="checkbox" 
                  checked={preferences.functional} 
                  onChange={() => handlePreferenceChange('functional')}
                  className="opacity-0 w-0 h-0 absolute"
                  id="toggle-functional"
                />
                <label 
                  htmlFor="toggle-functional" 
                  className={`block overflow-hidden h-5 rounded-full bg-gray-300 cursor-pointer ${preferences.functional ? 'bg-ta-green' : ''}`}
                >
                  <span className={`block h-5 w-5 rounded-full bg-white transform transition-transform ${preferences.functional ? 'translate-x-3' : 'translate-x-0'}`}></span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-900">Analytics</p>
                <p className="text-[10px] text-gray-500">Help us improve</p>
              </div>
              <div className="relative inline-block w-8 mr-1 align-middle">
                <input 
                  type="checkbox" 
                  checked={preferences.analytics} 
                  onChange={() => handlePreferenceChange('analytics')}
                  className="opacity-0 w-0 h-0 absolute"
                  id="toggle-analytics"
                />
                <label 
                  htmlFor="toggle-analytics" 
                  className={`block overflow-hidden h-5 rounded-full bg-gray-300 cursor-pointer ${preferences.analytics ? 'bg-ta-green' : ''}`}
                >
                  <span className={`block h-5 w-5 rounded-full bg-white transform transition-transform ${preferences.analytics ? 'translate-x-3' : 'translate-x-0'}`}></span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-900">Advertising</p>
                <p className="text-[10px] text-gray-500">Relevant ads</p>
              </div>
              <div className="relative inline-block w-8 mr-1 align-middle">
                <input 
                  type="checkbox" 
                  checked={preferences.advertising} 
                  onChange={() => handlePreferenceChange('advertising')}
                  className="opacity-0 w-0 h-0 absolute"
                  id="toggle-advertising"
                />
                <label 
                  htmlFor="toggle-advertising" 
                  className={`block overflow-hidden h-5 rounded-full bg-gray-300 cursor-pointer ${preferences.advertising ? 'bg-ta-green' : ''}`}
                >
                  <span className={`block h-5 w-5 rounded-full bg-white transform transition-transform ${preferences.advertising ? 'translate-x-3' : 'translate-x-0'}`}></span>
                </label>
              </div>
            </div>
          </div>
        )}
        
        <div className="text-[10px] text-gray-500 mb-3">
          <p>
            Read our <Link to="/cookie-policy" className="text-ta-green hover:underline">Cookie Policy</Link> for more details.
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {showDetails ? (
            <button
              onClick={handleSavePreferences}
              className="px-3 py-1.5 bg-ta-green text-white text-xs font-medium rounded-md hover:bg-ta-green-dark transition-colors"
            >
              Save Preferences
            </button>
          ) : (
            <>
              <button
                onClick={handleAcceptAll}
                className="px-3 py-1.5 bg-ta-green text-white text-xs font-medium rounded-md hover:bg-ta-green-dark transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleRejectNonEssential}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-50 transition-colors"
              >
                Essential Only
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;